import React, { useEffect, useMemo, useState } from 'react'
import CommonCard from '../../../../global/card'
import CommonSkeletonLoader from '../../../../global/loading'
import { useLocation } from 'react-router-dom'
import SOURCES from '../../../../../utils/constants'
import { useGenericQuery } from '../../../../../queries/use-generic-query'
import { QUERY_KEYS } from '../../../../../lib/query-constants'
import useDebounce from '../../../../../utils/customHooks'
import EnhancedLeadsImportredList from './enhancedLeadsImportredList'
import LeadDetail from '../../../modules/leadDetail'
import CommonPagination from '../../../../global/pagination'
import EnhancedAdvanceSearchLeadsImported from './enhancedAdvanceSearchLeadsImported'

const EnhancedLeadsImported = () => {
    const { DEFAULT_RECORDS_LISTING, DEFAULT_RECORDS_SEARCH } = SOURCES;

    const [searchLeadsImported, setSearchLeadsImported] = useState("")
    const [leadsImportedViewType, setLeadsImportedViewType] = useState(() => {
        return localStorage.getItem("leadsImportedViewType") || "GRID";
    });
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
    const [leadDetail, setLeadDetail] = useState(null);
    const [showDetail, setshowDetail] = useState(false);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        10
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const [paginationSearch, setPaginationSearch] = useState({
        offsetSearch: 0,
        limitSearch: SOURCES.DEFAULT_RECORDS_SEARCH,
    });

    const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
    const [advanceSearch, setAdvanceSearch] = useState({
        advance_search_enabled:
            SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
        startDate: "",
        endDate: "",
        leadSource: "",
        searchTerm: "",
        user: "",
    });

    const debouncedSearchTerm = useDebounce(searchLeadsImported, 500);

    // Key params for main listing
    const keyParamsForLeadInfo = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };

    // Key params for search
    const searchKeyParams = {
        offset: paginationSearch.offsetSearch,
        limit: paginationSearch.limitSearch,
        q: debouncedSearchTerm,
        advance_search: advanceSearch.advance_search_enabled,
        startdate: advanceSearch.startDate,
        enddate: advanceSearch.endDate,
        leadsource: advanceSearch.leadSource,
        term: advanceSearch.searchTerm,
        userEmail: advanceSearch.user
    };

    // Main leads data query
    const { data: leadsData, isLoading: leadsDataLoading, isFetching: leadsDataFetching } = useGenericQuery({
        endpoint: "leadinfo",
        queryKey: QUERY_KEYS.leadsImported.lists({ ...keyParamsForLeadInfo }),
        queryParams: { ...keyParamsForLeadInfo },
        onError: () => { },
        onSuccess: () => { },
        // enabled: !debouncedSearchTerm &&
        //     advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER
    });

    // Search data query
    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "leadinfo/search",
        queryKey: QUERY_KEYS.leadsImported.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        onError: () => { },
        onSuccess: () => { },
        enabled: !!debouncedSearchTerm.trim() ||
            (advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER &&
                advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_CLOSED_SEARCH_FORM)
    });

    // Lead source options (for future advance search)
    const { data: leadSourceOptions, isLoading: leadSourceOptionsLoading } = useGenericQuery({
        endpoint: "leadsource/options",
        queryKey: QUERY_KEYS.leadsImported.item("leadsource/options"),
        onError: () => { },
        onSuccess: () => { },
    });

    // Determine final data to display
    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm ||
            (advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER &&
                advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_CLOSED_SEARCH_FORM)) {
            return searchData?.results || [];
        }
        return leadsData?.results || [];
    }, [leadsData, searchData, debouncedSearchTerm, advanceSearch.advance_search_enabled]);

    const isLoading = leadsDataLoading || searchDataLoading || leadSourceOptionsLoading;
    const dataExist = getFinalData && getFinalData.length > 0;

    useEffect(() => {
        setAdvanceSearch((prev) => ({
            ...prev,
            advance_search_enabled:
                SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
        }));
        setShowAdvanceSearch(false);
    }, [searchLeadsImported]);

    const viewDetail = (item) => {
        setLeadDetail(item);
        setshowDetail(true);
    };

    return (
        <>
            <CommonCard
                isSearch={{
                    show: true,
                    placeholder: "Search leads imported...",
                    searchInput: searchLeadsImported,
                    setSearchInput: setSearchLeadsImported
                }}
                isListGridView={{
                    show: true,
                    viewType: leadsImportedViewType,
                    setViewType: setLeadsImportedViewType,
                    viewFor: "leadsImportedViewType"
                }}
                isLoading={{
                    show: isLoading,
                    loaderCmp: (
                        <CommonSkeletonLoader
                            rows={9}
                            type={leadsImportedViewType === "GRID" ? "card" : "table"}
                            tableLength={8}
                        />
                    ),
                }}
                isAdvanceSearch={{
                    show: true,
                    children: <EnhancedAdvanceSearchLeadsImported
                        isOpen={showAdvanceSearch}
                        advanceSearch={advanceSearch}
                        setShowAdvanceSearch={setShowAdvanceSearch}
                        setAdvanceSearch={setAdvanceSearch}
                        getAdvanceSearchData={() => { }}
                    />
                }}
                className={(!isLoading && leadsDataFetching) && "opacity-50"}
            >
                <EnhancedLeadsImportredList
                    isLoading={isLoading}
                    leadsImported={getFinalData}
                    viewDetail={viewDetail}
                    sort={sort}
                    setSort={setSort}
                    viewType={leadsImportedViewType}
                />

                {(dataExist && !searchLeadsImported &&
                    advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER) &&
                    leadsData?.totalReords > DEFAULT_RECORDS_LISTING && (
                        <CommonPagination
                            currentPage={pagination.page}
                            defaultLimit={pagination.limit}
                            totalRecords={leadsData?.totalReords}
                            setCurrentPage={setPagination}
                            activePageRecords={getFinalData?.length || 0}
                            paginationFor="Leads Imported"
                            className="mt-4"
                        />
                    )}
            </CommonCard>

        </>
    )
}

export default EnhancedLeadsImported